import { Question } from '../types';
import { questions } from '../config/questions';
import { AnswersState } from '../types';

export const getQuestionsWithAnswers = (
  providedAnswers: AnswersState, 
  cinematicMode: boolean
): Question[] => {
  const selectedOutputType = providedAnswers.outputType;
  
  if (!selectedOutputType) return questions.slice(0, 1); // Only show outputType
  
  return questions.filter(question => {
    // Always show output type and start type
    if (question.id === 'outputType' || question.id === 'startType') return true;
    
    // Visual genre - optional for all types except music
    if (question.id === 'visualGenre') {
      return selectedOutputType !== 'music';
    }
    
    // Scene reference - only show if genre is selected, and filter by genre
    if (question.id === 'sceneReference') {
      return providedAnswers.visualGenre && selectedOutputType !== 'music';
    }
    
    // Quantity question - only for video(s) and image(s)
    if (question.id === 'quantity') {
      return providedAnswers.outputType === 'video' || providedAnswers.outputType === 'image';
    }
    
    // Upload prompt step - only show if user selected continue options
    if (question.id === 'uploadReference') {
      return providedAnswers.startType === 'continue-image' || providedAnswers.startType === 'continue-video';
    }
    
    // Upload questions based on start type (these are now removed)
    if (isUploadQuestion(question, providedAnswers)) return true;
    
    // Basic questions - always relevant
    if (isBasicQuestion(question.id)) return true;
    
    // Character count - optional for all visual content types
    if (question.id === 'characterCount') {
      return selectedOutputType !== 'music';
    }
    
    // Type-specific questions
    if (isTypeSpecificQuestion(question, selectedOutputType as string)) return true;
    
    // Cinematic mode questions
    if (isCinematicQuestion(question, selectedOutputType as string, cinematicMode)) return true;
    
    return false;
  });
};

const isUploadQuestion = (question: Question, answers: AnswersState): boolean => {
  if (question.id === 'previousSceneVideo') {
    return answers.startType === 'video';
  }
  if (question.id === 'previousSceneImage') {
    return answers.startType === 'image' || answers.startType === 'style-image';
  }
  if (question.id === 'styleVideoUpload') {
    return answers.startType === 'style-video';
  }
  return false;
};

const isBasicQuestion = (questionId: string): boolean => {
  return ['contentType', 'mood'].includes(questionId);
};

const isTypeSpecificQuestion = (question: Question, outputType: string): boolean => {
  // Duration - only for video, music, shotlist, package options
  if (question.id === 'duration') {
    return ['video', 'music', 'shotlist', 'package'].includes(outputType);
  }
  
  // Camera style - only for video, shotlist, package options
  if (question.id === 'cameraStyle') {
    return ['video', 'shotlist', 'package'].includes(outputType);
  }
  
  // Image composition - for image types and package
  if (question.id === 'imageComposition') {
    return ['image', 'package'].includes(outputType);
  }
  
  // Art style - for image, video, shotlist, package options
  if (question.id === 'artStyle') {
    return ['image', 'video', 'shotlist', 'package'].includes(outputType);
  }
  
  return false;
};

const isCinematicQuestion = (question: Question, outputType: string, cinematicMode: boolean): boolean => {
  const cinematicQuestions = ['characterName', 'characterFeatures', 'cameraSpecs', 'lensType', 'shotStructure', 'audioElements', 'environmentalSetup', 'keyElements'];
  
  if (!cinematicQuestions.includes(question.id) || !cinematicMode) return false;
  
  // Character questions - for video and packages
  if (question.id === 'characterName' || question.id === 'characterFeatures') {
    return ['video', 'package', 'shotlist'].includes(outputType);
  }
  
  // Camera specs and lens - for video, image, shotlist, package options (not music)
  if (question.id === 'cameraSpecs' || question.id === 'lensType') {
    return ['video', 'image', 'shotlist', 'package'].includes(outputType);
  }
  
  // Shot structure - for video, image, shotlist, package options
  if (question.id === 'shotStructure') {
    return ['video', 'image', 'shotlist', 'package'].includes(outputType);
  }
  
  // Audio elements - for video, music, shotlist, package (not images)
  if (question.id === 'audioElements') {
    return ['video', 'music', 'shotlist', 'package'].includes(outputType);
  }
  
  // Environmental setup - for video, image, shotlist, package options (not music)
  if (question.id === 'environmentalSetup') {
    return ['video', 'image', 'shotlist', 'package'].includes(outputType);
  }
  
  // Key elements - for all types
  if (question.id === 'keyElements') return true;
  
  return false;
};
